/**
 * "Schicht zum Tausch anbieten" (Phase E, Kapitel 6) — gezielt an eine
 * Kollegin/einen Kollegen ODER offen an alle berechtigten Kolleg:innen am
 * Standort (bzw. an Standorten mit gegenseitigem allowCrossLocationSwap).
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../store/auth';
import { useQuery } from '@tanstack/react-query';
import { shiftSwapsApi } from '../lib/shiftSwaps';
import { useCreateSwap } from '../data/shiftSwaps';
import type { LocalShift } from '../db/schema';
import { ApiError } from '../lib/api';
import { hapticError, hapticSuccess } from '../lib/haptics';

interface Props {
  shift: LocalShift;
  onClose: () => void;
  onSaved: () => void;
}

export function SwapOfferModal({ shift, onClose, onSaved }: Props) {
  const token = useAuth((s) => s.token)!;
  const create = useCreateSwap();

  const { data: colleagues, isLoading } = useQuery({
    queryKey: ['swap-eligible', shift.id],
    queryFn: () => shiftSwapsApi.eligibleColleagues(token, shift.id),
  });

  const [target, setTarget] = useState<'open' | string>('open');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setTarget('open'), [shift.id]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        shiftId: shift.id,
        toUserId: target === 'open' ? null : target,
        note: note.trim() ? note.trim() : null,
      });
      hapticSuccess();
      onSaved();
    } catch (err) {
      hapticError();
      setError(err instanceof ApiError ? err.message : 'Angebot fehlgeschlagen');
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-2 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md max-h-[90vh] overflow-y-auto border border-border/60">
        <h3 className="text-lg font-semibold mb-1">Schicht zum Tausch anbieten</h3>
        <p className="text-xs text-slate-400 mb-4">
          {shift.shiftDate} · {shift.startTime}–{shift.endTime}
        </p>

        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">An wen?</span>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="input"
            >
              <option value="open">
                Offen — alle berechtigten Kolleg:innen werden benachrichtigt
              </option>
              {isLoading && <option disabled>— lädt … —</option>}
              {colleagues?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {!isLoading && colleagues?.length === 0 && (
              <p className="text-xs text-amber-300 mt-1">
                Keine bestimmte Person gefunden — nur "Offen" möglich. Prüfe ggf.
                Standort-Zuordnungen.
              </p>
            )}
          </label>

          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">Notiz (optional)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="input resize-y"
              placeholder="z. B. bin krank, Zahnarzttermin verschoben"
            />
          </label>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost w-full sm:w-auto">
              Abbrechen
            </button>
            <button type="submit" disabled={create.isPending} className="btn w-full sm:w-auto">
              {create.isPending ? 'Sende…' : 'Angebot senden'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
